import React, {
    createContext,
    useReducer,
    ReactNode,
    Dispatch,
    useContext,
} from 'react';
import { BaselineRange } from './typings';

export interface ReportData {
    baselineRange?: BaselineRange;
}
// Define the shape of the state
interface GlobalState {
    reportData: ReportData;
}

// Define the actions' types
type GlobalAction = { type: 'SET_DATA'; payload: ReportData };

// Define the type for the context value
interface GlobalContextType {
    state: GlobalState;
    dispatch: Dispatch<GlobalAction>;
}

// Initialize the context
const GlobalStateContext = createContext<GlobalContextType | undefined>(
    undefined,
);

// Define the initial state
const initialState: GlobalState = {
    reportData: {},
};

// Define the reducer
const reducer = (state: GlobalState, action: GlobalAction): GlobalState => {
    switch (action.type) {
        case 'SET_DATA':
            return {
                ...state,
                reportData: action.payload,
            };
        default:
            return state;
    }
};

// Define the type for the provider props
interface GlobalStateProviderProps {
    children: ReactNode;
    initialData?: any; // Adjust this type based on what "initialData" actually represents
}

// Create the provider component
export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({
    children,
    initialData,
}) => {
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        reportData: {
            ...initialState.reportData,
            ...initialData,
        },
    });

    return (
        <GlobalStateContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export default GlobalStateContext;

// Create and export a custom hook for consuming the report data context,
export const useReportData = () => {
    const context = useContext(GlobalStateContext);
    if (context === undefined) {
        throw new Error(
            'useReportData must be used within a GlobalStateContext',
        );
    }
    return context;
};
